import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  type EffortLevel,
  MANAGED_AI_PROVIDER_ID,
  resolveManagedChatModel,
} from "../../../shared/aiProviders";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { parseChatError } from "../../../shared/chatErrors";
import { CLIENT_AUTO_RESOLVED_TOOLS } from "../../../shared/clientAutoResolvedTools";
import { CONTEXT_LIMIT_MESSAGE } from "../../../shared/contextLimit";
import {
  HEARTBEAT_INTERVAL_MS,
  LEASE_EXPIRY_MS,
} from "../../../shared/runLease";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import useChatScroll from "../../hooks/useChatScroll";
import { useChatSounds } from "../../hooks/useChatSounds";
import { useCreateSkill } from "../../hooks/useCreateSkill";
import { useFavicon } from "../../hooks/useFavicon";
import { useFileDropzone } from "../../hooks/useFileDropzone";
import { useModelExperiment } from "../../hooks/useModelExperiment";
import { type ModelScope, useScopedModel } from "../../hooks/useScopedModel";
import { useFocusShortcut } from "../../hooks/useFocusShortcut";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePlanModeShortcut } from "../../hooks/usePlanModeShortcut";
import { useQuery } from "../../hooks/useQuery";
import { useSlashSkills } from "../../hooks/useSlashSkills";
import {
  abortChat,
  type StreamMessage,
  useStreamingText,
} from "../../hooks/useStreamingText";
import { cacheToolOutput } from "../../hooks/useToolOutput";
import type { PendingAttachment } from "../../lib/attachments";
import { maybeDisplayBrowserNotification } from "../../lib/browserNotifications";
import { getStatusState, type StatusState } from "../../lib/chatStatus";
import { useCaretToEndOnMount, useDraft } from "../../lib/draft";
import {
  loadEffort,
  resolveEffortFromEntries,
  saveEffort,
} from "../../lib/effort";
import {
  loadModelExperimentChatState,
  saveModelExperimentChatState,
} from "../../lib/modelExperiment";
import { formatElapsedTime } from "../../lib/formatElapsedTime";
import { formatHoverTime } from "../../lib/formatTimestamp";
import {
  type BashFileTarget,
  inputToFileEdit,
  inputToFileTarget,
  inputToFileWrite,
  type StreamingBashWrite,
  type StreamingFileEdit,
} from "../../lib/parseStreamingBashWrite";
import {
  loadPlanMode,
  resolveDefaultPlanMode,
  resolvePlanModeFromEntries,
  resolveSeededPlanMode,
  savePlanMode,
} from "../../lib/planMode";
import { playChatNeedsInput, playClick } from "../../lib/sounds";
import { attachmentPath } from "../../lib/apiPaths";
import { apiUrl } from "../../lib/tenantOrigin";
import { userDisplayName } from "../../lib/userDisplayName";
import { parseApprovePlanInput } from "../../tools/ApprovePlan";
import {
  AttachmentHoverPreview,
  attachmentActionIcon,
} from "../AttachmentHoverPreview";
import {
  AttachmentLabel,
  AttachmentPreview,
  AttachmentPreviewRow,
  AttachmentRowAnimator,
  attachmentFileIcon,
  Dropzone,
  marchingDashes,
} from "../AttachmentPreviews";
import { Avatar, getUserColor } from "../Avatar";
import { ServiceAccountAvatar } from "../ServiceAccountAvatar";
import { PressButton } from "../PressButton";
import { Button } from "../Button";
import { FlyingDot } from "../FlyingDot";
import { Icon, type IconName } from "../Icon";
import { InputContainer, WORKFLOW_INPUT_HEIGHT } from "../InputContainer";
import { Link } from "../Link";
import { PlanModeToggle } from "../PlanModeToggle";
import { StatusIndicator } from "../StatusIndicator";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import { Tooltip } from "../Tooltip";
import { VoiceRecorder } from "../VoiceRecorder";
import type { ChatEntryData, CodeSnippet, ElementSnippet } from "../Workflow";
import { ChatMessageNav } from "./ChatMessageNav";
import { ActivityLine } from "./ActivityLine";
import type { ChatTool } from "./ChatTool";
import { ComposerStrip } from "./ComposerStrip";
import { ComposerStripToggle } from "./ComposerStripToggle";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";
import { FollowNudge } from "./FollowNudge";
import { ErrorMessage } from "./ErrorMessage";
import { AccentBar, MessageContent } from "./MessageContent";
import { SlashMenu, type SuggestionSet } from "./SlashMenu";
import { SlashSkillCompletion } from "./SlashSkillCompletion";
import { type QueuedMessage, QueueStack } from "./QueueStack";
import { CompactionEntry } from "./CompactionEntry";
import { useChatQueue } from "./useChatQueue";
import { ModelFeedbackRow } from "./ModelFeedbackRow";
import { ToolUse } from "./ToolUse";
import { ToolUseGroup } from "./ToolUseGroup";
import { TypewriterText } from "./TypewriterText";
import { getActionDisplay, UserActionGroup } from "./UserActionGroup";
import { navigate, routes } from "../../lib/urls";
import { prependAttachedSkills } from "../../../shared/skills/attachedSkills";

const statusText = {
  waiting: "3B needs you",
  building: "3B is building",
  thinking: "3B is thinking",
  loading: "3B is connecting",
  resting: "What do you want to do?",
  dragging: "Let’s take a look",
} as const;

const planModeStatusText = {
  waiting: "3B needs your input",
  building: "3B is drafting a plan",
  thinking: "3B is planning",
  loading: "3B is gathering context",
  resting: "What should we plan?",
  dragging: "Let’s take a look",
} as const;

const activeStates: StatusState[] = [
  "thinking",
  "building",
  "loading",
  "waiting",
];

const kickoffDisplay = {
  autofix: { icon: "radar", label: "Autofix kickoff" },
  autotune: { icon: "audio-waveform", label: "Autotune kickoff" },
} satisfies Record<string, { icon: IconName; label: string }>;

const userMessageActionDisplay = {
  readme: { icon: "pencil", message: "asked 3B to add a readme" },
  error: { icon: "traffic-cone", message: "sent an error message to 3B" },
  connector: {
    icon: "api-key-custom",
    message: "asked 3B to set up this workflow as a connector",
  },
  investigate_step_test: {
    icon: "search",
    message: "asked 3B to investigate a failing step test",
  },
  step_test_fixture_setup: {
    icon: "database",
    message: "asked 3B to set up volume fixtures for a step test",
  },
} satisfies Record<string, { icon: IconName; message: string }>;

const systemMessageIconByPrefix: [prefix: string, icon: IconName][] = [
  ["Saved:", "save"],
  ["Restored", "history"],
  ["Workflow renamed", "pencil"],
];

function systemMessageIcon(text: string): IconName {
  if (text === CONTEXT_LIMIT_MESSAGE) return "brain";
  for (const [prefix, icon] of systemMessageIconByPrefix) {
    if (text.startsWith(prefix)) return icon;
  }
  return "info";
}

// Git-push system messages lead with the author's name, so we match the stable
// trailing phrase rather than a prefix.
function isPushMessage(text: string): boolean {
  return text.split("\n")[0]?.endsWith("pushed from local") ?? false;
}

const Container = styled("div")`
  display: flex;
  flex: 1;
  height: 0;
  flex-direction: column;
  position: relative;

  &[data-outer-scroll="true"] {
    flex: none;
    height: auto;
    min-height: 100%;
  }
`;

const Messages = styled("div")`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 56px 12px;
  margin-right: -12px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  > :nth-child(1) {
    margin-top: auto;
  }

  &[data-outer-scroll="true"] {
    height: auto;
    overflow-y: visible;
    overflow-x: visible;
    margin-right: 0;
  }
  &[data-outer-scroll="true"] > :nth-child(1) {
    margin-top: 0;
  }
`;

const Message = styled("div")`
  border-radius: 8px;
  max-width: 100%;
  min-width: 0;
  display: inline-flex;
  gap: 8px;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--text-primary);
`;

const UserMessageTimestamp = styled("div")`
  position: absolute;
  top: 100%;
  right: 12px;
  margin-top: 4px;
  font-family: inherit;
  font-size: 9px;
  line-height: 12px;
  font-weight: 450;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0;
  transform: translateY(-4px);
  transition:
    opacity 80ms ease-out,
    transform 80ms ease-out;
  pointer-events: none;
  white-space: nowrap;
`;

const UserMessageWrapper = styled("div")`
  position: relative;

  &:hover ${UserMessageTimestamp} {
    opacity: 0.3;
    transform: translateY(0);
    transition-delay: 600ms;
  }
`;

const ChatForm = styled("form")`
  display: flex;
  gap: 8px;
  padding: 0 4px 4px;

  &[data-outer-scroll="true"] {
    position: sticky;
    bottom: 0;
    background: var(--surface-primary);
    z-index: 1;
  }
`;

const TextareaContainer = styled("div")`
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  position: relative;
  isolation: isolate;
`;

const ScrollToBottomButton = styled(PressButton)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, calc(-100% - 12px));
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-primary);
  &:hover {
    background: var(--surface-secondary);
  }
`;

const StatusContainer = styled("div")`
  display: flex;
  gap: 6px;
  align-items: center;
  max-width: 100%;
  padding: 8px;
  border-radius: 12px;

  &[data-clickable="true"] {
    cursor: pointer;
  }
  &[data-clickable="true"]:hover {
    background: var(--alpha2);
  }
  &[data-follow-active="true"] {
    background: var(--alpha2);
    box-shadow: 0 0 0 1px var(--border-alpha) inset;
  }
`;

const FollowAvatarButton = styled("button")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: inherit;
`;

const RecordingIndicator = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  margin-top: -4px;
  margin-bottom: -4px;
  padding: 4px 6px;
  background: var(--alpha4);
  border-radius: 50px;
`;

const RecordingDot = styled("div")`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--red-600);
`;

const VoiceRecorderContainer = styled(InputContainer)`
  height: ${WORKFLOW_INPUT_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;

  > canvas {
    position: absolute;
    top: 12px;
    left: 12px;
    width: calc(100% - 24px) !important;
    height: calc(100% - 48px) !important;
  }
`;

const ComposerInputWrap = styled("div")`
  position: relative;
  &::after {
    content: "";
    position: absolute;
    left: 0.5px;
    right: 0.5px;
    bottom: 0.5px;
    height: 48px;
    z-index: 1;
    pointer-events: none;
    border-bottom-left-radius: 11.5px;
    border-bottom-right-radius: 11.5px;
    background: linear-gradient(
      to bottom,
      transparent,
      var(--surface-primary) 8px,
      var(--surface-primary)
    );
    opacity: 0;
  }
  &[data-show-fade="true"]::after {
    opacity: 1;
  }
`;

const ComposerControls = styled("div")`
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
`;

const ComposerControlGroup = styled("div")`
  display: flex;
  gap: 4px;
  align-items: center;
  pointer-events: auto;
`;

const checkpointLinkBase = `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 38px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  color: var(--text-secondary);
  text-decoration: none;
  opacity: 0;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    background 0.15s;
  & > span:first-child {
    transition: transform 0.4s ease;
  }
  &[data-visible="true"] {
    opacity: 1;
    transform: translateY(0);
  }
  &:hover {
    background: var(--surface-secondary);
  }
`;

const PrevCheckpointLink = styled(Link)`
  ${checkpointLinkBase}
  margin: 12px auto 4px;
  transform: translateY(-12px);
  &:hover > span:first-child {
    transform: rotate(-360deg);
  }
`;

const NextCheckpointLink = styled(Link)`
  ${checkpointLinkBase}
  margin: 20px auto 20px;
  transform: translateY(12px);
  &[data-visible="true"] {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 50ms;
  }
  &:hover > span:first-child {
    transform: rotate(360deg);
  }
`;

const BUBBLE_PADDING_Y = 16;
const TEXT_LINE_HEIGHT = 16;
const SINGLE_LINE_MAX = BUBBLE_PADDING_Y * 2 + TEXT_LINE_HEIGHT + 1;

function UserMessageBubble({
  text,
  isDictated,
  attachments,
  userColor,
}: {
  text: string;
  isDictated: boolean;
  attachments: ChatEntryWithSubTables["attachments"];
  userColor: string | null;
}) {
  const externalApiUrl = apiUrl();
  const ref = useRef<HTMLDivElement | null>(null);
  const [singleLine, setSingleLine] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (height: number) =>
      setSingleLine(height <= SINGLE_LINE_MAX);
    measure(el.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        measure(
          entry.borderBoxSize?.[0]?.blockSize ??
            entry.target.getBoundingClientRect().height
        );
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: userColor ? `var(--${userColor}-200)` : "var(--alpha4)",
        color: userColor ? `var(--${userColor}-1000)` : undefined,
        padding: BUBBLE_PADDING_Y,
        borderRadius: singleLine ? 9999 : 16,
        minWidth: 0,
      }}
    >
      {attachments.map((att) => (
        <AttachmentHoverPreview
          key={att.id}
          fileName={att.fileName}
          mediaType={att.mediaType}
          sizeBytes={att.sizeBytes}
          url={`${externalApiUrl}${attachmentPath(att.id)}`}
          inlineData={null}
        >
          <AttachmentLabel type="button">
            <span className="file-icon">
              <Icon name={attachmentFileIcon(att.mediaType)} size={10} />
            </span>
            <span className="download-icon">
              <Icon name={attachmentActionIcon(att.mediaType)} size={10} />
            </span>
            <Text variant="text-small-md" ellipsis>
              {att.fileName}
            </Text>
          </AttachmentLabel>
        </AttachmentHoverPreview>
      ))}
      <MessageContent text={text} isDictated={isDictated} />
    </div>
  );
}

const StopButton = styled(Button)`
  &.secondary {
    background: var(--alpha4);
    &:hover:not(:disabled) {
      background: var(--alpha8);
    }
  }
`;

const DateMarker = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const DateBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
`;

function formatDateLabel(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const entryDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (entryDay.getTime() === today.getTime()) return "Today";
  if (entryDay.getTime() === yesterday.getTime()) return "Yesterday";

  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  };
  return date.toLocaleDateString("en-US", options);
}

function getGroupTimestamp(
  grouped:
    | { type: "single"; entry: { createdAt: number } }
    | {
        type: "userActionGroup";
        entries: { createdAt: number }[];
      }
    | {
        type: "toolGroup";
        entries: { createdAt: number }[];
        reasoning?: { createdAt: number } | undefined;
      }
): number {
  if (grouped.type === "single") return grouped.entry.createdAt;
  if (grouped.type === "userActionGroup")
    return grouped.entries[0]?.createdAt ?? 0;
  if (grouped.entries[0]) return grouped.entries[0].createdAt;
  if (grouped.reasoning) return grouped.reasoning.createdAt;
  return 0;
}

function getDateKey(timestamp: number): string {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function CollapsibleSystemMessage({
  text,
  icon,
}: {
  text: string;
  icon: IconName;
}) {
  const [expanded, setExpanded] = useState(false);
  const lines = text.split("\n");
  const summary = lines[0];
  const details = lines.slice(1).filter(Boolean).join("\n");

  if (!details) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: 28,
          gap: 4,
          padding: "6px 10px 6px 8px",
          borderRadius: 8,
          width: "fit-content",
          marginTop: 16,
        }}
      >
        <Icon name={icon} size={12} color="var(--text-secondary)" />
        <Text variant="text-small" color="var(--text-secondary)">
          {summary}
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        marginTop: 16,
      }}
    >
      <CollapsibleHeader onPress={() => setExpanded((prev) => !prev)}>
        <CollapsibleHeaderInner>
          <ToggleIconContainer aria-hidden>
            <DefaultIcon>
              <Icon name={icon} size={12} color="var(--text-secondary)" />
            </DefaultIcon>
            <HoverIcon>
              <Icon
                name={expanded ? "minus" : "plus"}
                size={12}
                color="var(--text-secondary)"
              />
            </HoverIcon>
          </ToggleIconContainer>
          <Text variant="text-small" color="var(--text-secondary)">
            {summary}
          </Text>
        </CollapsibleHeaderInner>
      </CollapsibleHeader>
      <ExpandWrapper data-expanded={expanded}>
        <ExpandInner data-expanded={expanded}>
          <ExpandedContent>
            <Text variant="text-small" color="var(--text-secondary)">
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {details}
              </span>
            </Text>
          </ExpandedContent>
        </ExpandInner>
      </ExpandWrapper>
    </div>
  );
}

function MergeMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="merge" />;
}

function PushMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="laptop" />;
}

function GitSyncMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="git-branch" />;
}

function KickoffMessage({
  text,
  icon,
  label,
}: {
  text: string;
  icon: IconName;
  label: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <CollapsibleHeader onPress={() => setExpanded((prev) => !prev)}>
        <CollapsibleHeaderInner>
          <ToggleIconContainer aria-hidden>
            <DefaultIcon>
              <Icon name={icon} size={12} color="var(--text-secondary)" />
            </DefaultIcon>
            <HoverIcon>
              <Icon
                name={expanded ? "minus" : "plus"}
                size={12}
                color="var(--text-secondary)"
              />
            </HoverIcon>
          </ToggleIconContainer>
          <Text variant="text-small" color="var(--text-secondary)">
            {label}
          </Text>
        </CollapsibleHeaderInner>
      </CollapsibleHeader>
      <ExpandWrapper data-expanded={expanded}>
        <ExpandInner data-expanded={expanded}>
          <ExpandedContent>
            <Text variant="text-small" color="var(--text-secondary)">
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {text}
              </span>
            </Text>
          </ExpandedContent>
        </ExpandInner>
      </ExpandWrapper>
    </div>
  );
}

export type ChatSendPayload = {
  text: string;
  isDictated?: boolean;
  planMode?: boolean;
  effort?: EffortLevel;
  chatModel?: string;
  actionKind?:
    | "readme"
    | "error"
    | "connector"
    | "investigate_step_test"
    | "step_test_fixture_setup";
  attachments?: AttachmentPayload[];
  selectedStep?: string;
  interruptEntryId?: string;
};

export type ChatHandle = {
  enqueueOrSend: (payload: ChatSendPayload) => void;
};

export type ChatProps = {
  entries: readonly ChatEntryData[];
  writeChatId: string | null;
  draftKey: string;
  onSend: (payload: ChatSendPayload, onError: () => void) => void;
  onInterrupt: () => void;
  onProvideToolResult: (
    entryId: string,
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean; chatModel?: string }
  ) => void;
  prevCheckpointLink?: string;
  prevCheckpointLabel?: string;
  nextCheckpointLink?: string;
  nextCheckpointLabel?: string;
  isCurrentUserChat: boolean;
  canWrite: boolean;
  tools: readonly ChatTool[];
  suggestions: SuggestionSet;
  soundsEnabled: boolean;
  respondingAt: number | null;
  lastLoopHeartbeatAt?: number | null;
  awaitingAction: boolean;
  codeSnippets?: readonly CodeSnippet[];
  removeCodeSnippet?: (id: string) => void;
  clearCodeSnippets?: () => void;
  elementSnippets?: readonly ElementSnippet[];
  removeElementSnippet?: (id: string) => void;
  clearElementSnippets?: () => void;
  selectedStepName?: string;
  spaceId?: string;
  historyBanner?: React.ReactNode;
  /**
   * When set, replaces the chat’s tool-pending prompt with this node.
   * Used by the apps-CLI flow to inline a connect-app picker without
   * requiring an `AssistantToolUse` chat entry.
   */
  pendingPromptOverride?: React.ReactNode;
  outerScroll?: boolean;
  outerScrollRef?: React.RefObject<HTMLDivElement | null>;
  onStatusClick?: () => void;
  followActive?: boolean;
  showFollowNudge?: boolean;
  onDismissFollowNudge?: () => void;
  onStreamingToolStep?: (step: string | undefined) => void;
  onStatusChange?: (status: StatusState) => void;
  onStreamingFileWrite?: (write: StreamingBashWrite | null) => void;
  onStreamingFileEdit?: (edit: StreamingFileEdit | null) => void;
  onStreamingFileTarget?: (target: BashFileTarget | null) => void;
  planModeDefaultSurface?: "chat" | "workflow" | "branch" | "none";
  resolveFileHref?: (href: string) => string | null;
  emptyState?: (api: {
    fillInput: (text: string) => void;
    visible: boolean;
  }) => React.ReactNode;
  // Lead phrase for the empty composer; the “/ for skills and prompts” hint is
  // appended when a slash menu is available.
  placeholder?: string;
  modelScope: ModelScope;
  onZipFile?: (file: File) => void;
};

type ReadyAttachment = Omit<PendingAttachment, "data"> & { data: string };
export type AttachmentPayload = Omit<ReadyAttachment, "previewUrl">;

function toAttachmentPayload(
  readyAttachments: ReadyAttachment[]
): AttachmentPayload[] | undefined {
  if (readyAttachments.length === 0) return undefined;
  return readyAttachments.map(({ previewUrl: _, ...rest }) => rest);
}

function getStatusDisplayText(
  statusState: StatusState,
  elapsedMs: number,
  planMode: boolean
): string {
  if (statusState === "thinking") {
    if (planMode) {
      if (elapsedMs > 30_000) return "3B is refining the plan";
      if (elapsedMs > 15_000) return "3B is thinking through the plan";
    } else {
      if (elapsedMs > 30_000) return "3B is deep in thought";
      if (elapsedMs > 15_000) return "3B is pondering";
    }
  }
  return planMode ? planModeStatusText[statusState] : statusText[statusState];
}

// The status label is time-dependent (elapsed-time phrasing, and the
// reconnecting override below), and a dead server produces no re-renders,
// so the label ticks on its own clock.
function StatusLabel({
  statusState,
  respondingAt,
  lastLoopHeartbeatAt,
  planMode,
}: {
  statusState: StatusState;
  respondingAt: number | null;
  lastLoopHeartbeatAt: number | null | undefined;
  planMode: boolean;
}) {
  const [, tick] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const timer = setInterval(tick, 1_000);
    return () => clearInterval(timer);
  }, []);
  // A running turn heartbeats every few seconds; three missed beats — one
  // shy of the takeover threshold — means its server died and another is
  // about to claim and resume the run.
  const heartbeatAt = Math.max(respondingAt ?? 0, lastLoopHeartbeatAt ?? 0);
  if (
    respondingAt != null &&
    statusState !== "waiting" &&
    statusState !== "resting" &&
    Date.now() - heartbeatAt > LEASE_EXPIRY_MS - HEARTBEAT_INTERVAL_MS
  ) {
    return <>3B is reconnecting</>;
  }
  return (
    <>
      {getStatusDisplayText(
        statusState,
        respondingAt ? Date.now() - respondingAt : 0,
        planMode
      )}
    </>
  );
}

function formatStatusTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((ms - totalSeconds * 1000) / 100);
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, "0")}.${tenths}s`;
  }
  return `${seconds}.${tenths}s`;
}

function StatusTimer({ respondingAt }: { respondingAt: number }) {
  const [, tick] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      tick();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <Text variant="text-small" color="var(--text-secondary)">
      {formatStatusTime(Math.max(0, Date.now() - respondingAt))}
    </Text>
  );
}

function notifyChatDone(messages: ReadonlyMap<string, StreamMessage>): void {
  const text = [...messages.values()].findLast(
    (m) => m.role === "assistant" && m.text
  )?.text;
  if (!text) return;
  maybeDisplayBrowserNotification(
    "3B",
    text.replace(/\s+/g, " ").trim().slice(0, 200)
  );
}

export const Chat = forwardRef<ChatHandle, ChatProps>(function Chat(
  {
    entries,
    writeChatId,
    draftKey,
    onSend,
    onInterrupt,
    onProvideToolResult,
    prevCheckpointLink,
    prevCheckpointLabel,
    nextCheckpointLink,
    nextCheckpointLabel,
    isCurrentUserChat,
    canWrite,
    tools,
    suggestions,
    soundsEnabled,
    respondingAt,
    lastLoopHeartbeatAt,
    awaitingAction,
    codeSnippets,
    removeCodeSnippet,
    clearCodeSnippets,
    elementSnippets,
    removeElementSnippet,
    clearElementSnippets,
    selectedStepName,
    spaceId,
    historyBanner,
    pendingPromptOverride,
    outerScroll = false,
    outerScrollRef,
    onStatusClick,
    followActive = false,
    showFollowNudge = false,
    onDismissFollowNudge,
    onStreamingToolStep,
    onStatusChange,
    onStreamingFileWrite,
    onStreamingFileEdit,
    onStreamingFileTarget,
    planModeDefaultSurface = "chat",
    resolveFileHref,
    emptyState,
    placeholder = "Ask AI to build",
    modelScope,
    onZipFile,
  },
  forwardedRef
) {
  const externalApiUrl = apiUrl();
  const isResponding = respondingAt != null;
  const { messages: streamingEntries, runStatus } = useStreamingText(
    writeChatId,
    isResponding,
    notifyChatDone
  );

  const streamingToolStep = useMemo(() => {
    let latest: string | undefined;
    for (const m of streamingEntries.values()) {
      if (m.role !== "tool") continue;
      const step = (m.toolInput as { step?: string } | undefined)?.step;
      if (step) latest = step;
    }
    return latest;
  }, [streamingEntries]);
  useEffect(() => {
    onStreamingToolStep?.(streamingToolStep);
  }, [streamingToolStep, onStreamingToolStep]);

  // Only the latest tool entry's input matters — older entries linger in the
  // streaming map and would otherwise shadow a newer tool.
  const lastStreamingToolInput = useMemo(() => {
    let latest: Record<string, unknown> | null = null;
    for (const m of streamingEntries.values()) {
      if (m.role !== "tool") continue;
      latest = (m.toolInput as Record<string, unknown> | undefined) ?? null;
    }
    return latest;
  }, [streamingEntries]);

  const streamingFileWrite = useMemo<StreamingBashWrite | null>(
    () => inputToFileWrite(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileWrite?.(streamingFileWrite);
  }, [streamingFileWrite, onStreamingFileWrite]);

  const streamingFileEdit = useMemo<StreamingFileEdit | null>(
    () => inputToFileEdit(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileEdit?.(streamingFileEdit);
  }, [streamingFileEdit, onStreamingFileEdit]);

  const streamingFileTarget = useMemo<BashFileTarget | null>(
    () => inputToFileTarget(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileTarget?.(streamingFileTarget);
  }, [streamingFileTarget, onStreamingFileTarget]);

  const z = useZero();
  const [preferences] = useQuery(queries.userPreferences());
  const followUpBehavior =
    preferences?.followUpBehavior === "steer" ? "steer" : "queue";
  const resolveForce = (modifier: boolean) =>
    followUpBehavior === "steer" ? !modifier : modifier;
  const defaultPlanMode =
    planModeDefaultSurface === "none"
      ? false
      : resolveDefaultPlanMode(preferences, planModeDefaultSurface);
  const [input, setInput] = useDraft(draftKey);
  const historyPlanMode = useMemo(
    () => resolvePlanModeFromEntries(entries),
    [entries]
  );
  const hasPlanModeHistory = useMemo(
    () =>
      entries.some(
        (entry) =>
          entry.entryType === "UserMessage" &&
          entry.userMessage?.planMode != null
      ),
    [entries]
  );
  const [planMode, setPlanMode] = useState(
    () => historyPlanMode || loadPlanMode(writeChatId) === true
  );
  const planModeChatIdRef = useRef<string | null>(writeChatId);
  const planModeTouchedRef = useRef(false);
  useEffect(() => {
    const chatChanged = planModeChatIdRef.current !== writeChatId;
    if (chatChanged) planModeTouchedRef.current = false;
    planModeChatIdRef.current = writeChatId;
    if (chatChanged || !planModeTouchedRef.current) {
      setPlanMode(
        resolveSeededPlanMode({
          hasHistory: hasPlanModeHistory,
          historyPlanMode,
          savedPlanMode: loadPlanMode(writeChatId),
          defaultPlanMode,
          surface: planModeDefaultSurface,
        })
      );
    }
  }, [
    writeChatId,
    historyPlanMode,
    hasPlanModeHistory,
    defaultPlanMode,
    planModeDefaultSurface,
  ]);
  const togglePlanMode = useCallback(() => {
    planModeTouchedRef.current = true;
    setPlanMode((on) => {
      const next = !on;
      savePlanMode(writeChatId, next);
      return next;
    });
  }, [writeChatId]);
  usePlanModeShortcut(togglePlanMode);
  const historyEffort = useMemo(
    () => resolveEffortFromEntries(entries),
    [entries]
  );
  const [localEffort, setLocalEffort] = useState<EffortLevel | null>(() =>
    loadEffort(writeChatId)
  );
  const [effortChatId, setEffortChatId] = useState(writeChatId);
  if (effortChatId !== writeChatId) {
    setEffortChatId(writeChatId);
    setLocalEffort(loadEffort(writeChatId));
  }
  const effort = localEffort ?? historyEffort;
  const [chatModel, setChatModel] = useState<string | null>(null);
  // A workflow conversation spans a chat per commit, so the branch id is the
  // stable thread key; a personal chat is a single chat.
  const experimentModel = useModelExperiment(
    modelScope.type === "branch" ? modelScope.branchId : writeChatId
  );
  const effectiveChatModel = chatModel ?? experimentModel;
  const [experimentState, setExperimentState] = useState(() =>
    loadModelExperimentChatState(writeChatId)
  );
  const [feedbackJustSubmitted, setFeedbackJustSubmitted] = useState(false);
  const [experimentChatId, setExperimentChatId] = useState(writeChatId);
  if (experimentChatId !== writeChatId) {
    setExperimentChatId(writeChatId);
    setExperimentState(loadModelExperimentChatState(writeChatId));
    setFeedbackJustSubmitted(false);
  }
  const handleChatModelChange = (next: string | null) => {
    if (experimentModel && writeChatId) {
      const from = resolveManagedChatModel(effectiveChatModel);
      const to = next == null ? null : resolveManagedChatModel(next);
      const started = entries.some((e) => e.entryType === "UserMessage");
      if (started && to !== from) {
        const state = { ...experimentState, switched: true };
        setExperimentState(state);
        saveModelExperimentChatState(writeChatId, state);
        if (to) {
          z.mutate(
            sharedMutators.recordModelExperimentEvent({
              chatId: writeChatId,
              kind: "switch",
              model: to,
              fromModel: from,
            })
          );
        }
      }
    }
    setChatModel(next);
  };
  const [selectedQueueItem, setSelectedQueueItem] = useState<{
    index: number;
    button: "cancel" | "edit" | "send";
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [celebrateTrigger, setCelebrateTrigger] = useState<number | null>(null);
  const celebrateCountRef = useRef(0);
  useEffect(() => {
    const count =
      input.match(
        /\b(party|finally|magic|wow|genius|beautiful|actually\s+works)\b/gi
      )?.length ?? 0;
    if (count > celebrateCountRef.current) {
      setCelebrateTrigger(Date.now());
    }
    celebrateCountRef.current = count;
  }, [input]);

  const isGroupChat = useMemo(() => {
    const authorIds = new Set<string>();
    for (const entry of entries) {
      const authorId =
        entry.userMessage?.user?.id ?? entry.userMessage?.serviceAccount?.id;
      if (authorId) authorIds.add(authorId);
      if (authorIds.size > 1) return true;
    }
    return false;
  }, [entries]);

  const { providerId: modelProviderId, effectiveKey: modelEffectiveKey } =
    useScopedModel(modelScope, {
      chatModel: effectiveChatModel,
    });
  const {
    pendingAttachments,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  } = useFileDropzone({
    enabled: isCurrentUserChat && canWrite,
    providerId: modelProviderId,
    onZipFile,
  });

  useEffect(() => {
    if (isDragging && soundsEnabled) playChatNeedsInput();
  }, [isDragging, soundsEnabled]);

  const statusState = getStatusState(
    entries as ChatEntryWithSubTables[],
    isResponding,
    runStatus,
    awaitingAction
  );
  const isActiveStatus = activeStates.includes(statusState);

  const modelFeedbackEligible =
    experimentModel != null &&
    writeChatId != null &&
    isCurrentUserChat &&
    canWrite &&
    modelEffectiveKey === MANAGED_AI_PROVIDER_ID &&
    !experimentState.switched &&
    (experimentState.feedback == null || feedbackJustSubmitted);
  const submitModelFeedback = (feedback: "up" | "down") => {
    if (!writeChatId) return;
    const state = { ...experimentState, feedback };
    setExperimentState(state);
    saveModelExperimentChatState(writeChatId, state);
    setFeedbackJustSubmitted(true);
    z.mutate(
      sharedMutators.recordModelExperimentEvent({
        chatId: writeChatId,
        kind: "feedback",
        model: resolveManagedChatModel(effectiveChatModel),
        feedback,
      })
    );
  };
  const [isTypewriterAnimating, setIsTypewriterAnimating] = useState(false);
  useChatSounds(statusState, isTypewriterAnimating, soundsEnabled);
  useFavicon(
    statusState,
    isTypewriterAnimating,
    modelScope.type === "personal" ? "chat" : "workflow"
  );
  useEffect(() => {
    onStatusChange?.(statusState);
  }, [statusState, onStatusChange]);

  const [scrollContainer, resetScroll, isScrolledUp] = useChatScroll(
    outerScroll ? outerScrollRef : undefined
  );

  const messagesWrapperRef = useRef<HTMLDivElement | null>(null);
  const [queueOverlayHeight, setQueueOverlayHeight] = useState(0);
  const queueOverlayObserverRef = useRef<ResizeObserver | null>(null);
  const setQueueOverlayRef = useCallback((el: HTMLDivElement | null) => {
    queueOverlayObserverRef.current?.disconnect();
    queueOverlayObserverRef.current = null;
    if (!el) {
      setQueueOverlayHeight(0);
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setQueueOverlayHeight(entry?.contentRect.height ?? 0);
    });
    observer.observe(el);
    queueOverlayObserverRef.current = observer;
  }, []);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const [topVisible, setTopVisible] = useState(false);
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = scrollContainer.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTopVisible(entry?.isIntersecting ?? false),
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainer]);

  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
  const [bottomVisible, setBottomVisible] = useState(false);
  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    const root = scrollContainer.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setBottomVisible(entry?.isIntersecting ?? false),
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainer]);

  const handleTypewriterAnimatingChange = useCallback((animating: boolean) => {
    setIsTypewriterAnimating(animating);
  }, []);
  const isMobile = useIsMobile();
  const textareaRef = useFocusShortcut<HTMLTextAreaElement>();
  useCaretToEndOnMount(textareaRef);
  const [isTextareaFocused, setIsTextareaFocused] = useState(true);
  const [showComposerFade, setShowComposerFade] = useState(false);
  const updateComposerFade = useCallback(() => {
    const el = textareaRef.current;
    setShowComposerFade(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [textareaRef]);
  const scrollComposerToEnd = useCallback(() => {
    const el = textareaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    updateComposerFade();
  }, [textareaRef, updateComposerFade]);

  const fillAnimationRef = useRef<number | null>(null);
  const fillInput = useCallback(
    (text: string) => {
      if (fillAnimationRef.current !== null) {
        cancelAnimationFrame(fillAnimationRef.current);
      }
      // Type into the composer at the same cadence as the space-list animated
      // placeholder, so a picked prompt animates in rather than snapping in.
      const charsPerSecond = 188;
      let startTime: number | null = null;
      const step = (time: number) => {
        if (startTime === null) startTime = time;
        const revealed = Math.min(
          text.length,
          Math.floor(((time - startTime) / 1000) * charsPerSecond)
        );
        setInput(text.slice(0, revealed));
        const el = textareaRef.current;
        if (el) {
          // Re-focus each frame: the picked card takes focus on pointerdown and
          // the empty state unmounts mid-animation, so the composer would
          // otherwise not be focused when the typing finishes.
          el.focus({ preventScroll: true });
          el.setSelectionRange(revealed, revealed);
        }
        scrollComposerToEnd();
        fillAnimationRef.current =
          revealed < text.length ? requestAnimationFrame(step) : null;
      };
      fillAnimationRef.current = requestAnimationFrame(step);
    },
    [textareaRef, scrollComposerToEnd, setInput]
  );

  useEffect(() => {
    return () => {
      if (fillAnimationRef.current !== null) {
        cancelAnimationFrame(fillAnimationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(updateComposerFade);
  }, [updateComposerFade]);

  const snippetCount =
    (codeSnippets?.length ?? 0) + (elementSnippets?.length ?? 0);
  const prevSnippetCount = useRef(snippetCount);
  useEffect(() => {
    if (snippetCount > prevSnippetCount.current && !isMobile) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    prevSnippetCount.current = snippetCount;
  }, [snippetCount, isMobile, textareaRef]);

  // Focus the composer when a fresh chat opens (e.g. the "+" new-chat button),
  // deferred so it wins the focus race against the button that was just pressed.
  const didFocusNewChat = useRef(false);
  useEffect(() => {
    if (didFocusNewChat.current) return;
    if (isMobile || !canWrite || entries.length > 0) return;
    didFocusNewChat.current = true;
    const raf = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isMobile, canWrite, entries.length, textareaRef]);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null
  );
  const [recordingElapsedTime, setRecordingElapsedTime] = useState(0);
  const [expandedToolUse, setExpandedToolUse] = useState<string | null>(null);
  const [expandedToolGroups, setExpandedToolGroups] = useState<Set<string>>(
    new Set()
  );
  const [expandedUserActionGroups, setExpandedUserActionGroups] = useState<
    Set<string>
  >(new Set());
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { canCreate: canCreateSkill, createSkill } = useCreateSkill(spaceId);

  const {
    availableSkills,
    selectedSkills,
    addSkill,
    removeSkill,
    clearSkills,
    syncSlashToken,
    slashCompletion,
    handleSlashKeyDown,
  } = useSlashSkills({
    spaceId,
    textareaRef,
    value: input,
    setValue: setInput,
    onAfterAccept: scrollComposerToEnd,
  });

  const toolsByName = useMemo(
    () => new Map(tools.map((t) => [t.name, t])),
    [tools]
  );

  const scrollToBottom = useCallback(() => {
    const container = scrollContainer.current;
    if (!container) return;
    const distance =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distance < 4) {
      resetScroll();
      return;
    }
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    // no scrollend on iOS Safari; useChatScroll's scroll listener resets there
    container.addEventListener("scrollend", resetScroll, { once: true });
  }, [scrollContainer, resetScroll]);

  useEffect(() => {
    if (!recordingStartTime) return;
    const interval = setInterval(
      () => setRecordingElapsedTime(Date.now() - recordingStartTime),
      1000
    );
    return () => clearInterval(interval);
  }, [recordingStartTime]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  useEffect(resetScroll, []);

  // Group consecutive tool use entries when AI is done (resting)
  type GroupedEntry =
    | { type: "single"; entry: ChatEntryWithSubTables }
    | {
        type: "userActionGroup";
        entries: ChatEntryWithSubTables[];
        groupId: string;
      }
    | {
        type: "toolGroup";
        entries: ChatEntryWithSubTables[];
        groupId: string;
        reasoning?: ChatEntryWithSubTables;
        isTrailing?: boolean; // true if this is the active group being streamed
      };

  const groupedEntries = useMemo((): GroupedEntry[] => {
    const allEntries = entries as ChatEntryWithSubTables[];
    const filtered = allEntries.filter(
      (e, i) =>
        e.entryType !== "UserInterrupt" &&
        !(
          e.entryType === "AssistantReasoning" &&
          !e.assistantReasoning?.signature &&
          !(i === allEntries.length - 1 && statusState !== "resting")
        )
    );

    const result: GroupedEntry[] = [];
    let currentToolGroup: ChatEntryWithSubTables[] = [];
    let currentUserActionGroup: ChatEntryWithSubTables[] = [];
    let pendingReasoning: ChatEntryWithSubTables | undefined;

    const flushUserActionGroup = () => {
      const first = currentUserActionGroup[0];
      if (first) {
        result.push({
          type: "userActionGroup",
          entries: currentUserActionGroup,
          groupId: first.id,
        });
        currentUserActionGroup = [];
      }
    };

    const flushToolGroup = () => {
      const firstTool = currentToolGroup[0];
      if (firstTool) {
        result.push({
          type: "toolGroup",
          entries: currentToolGroup,
          groupId: firstTool.id,
          reasoning: pendingReasoning,
        });
        currentToolGroup = [];
        pendingReasoning = undefined;
      }
    };

    for (const entry of filtered) {
      if (
        entry.entryType === "UserAction" &&
        entry.userAction?.actionType !== "pushDraftToLive"
      ) {
        flushToolGroup();
        if (pendingReasoning) {
          result.push({
            type: "toolGroup",
            entries: [],
            groupId: pendingReasoning.id,
            reasoning: pendingReasoning,
          });
          pendingReasoning = undefined;
        }
        currentUserActionGroup.push(entry);
      } else if (
        entry.entryType === "AssistantToolUse" &&
        entry.toolCall?.toolName === "approvePlan"
      ) {
        flushUserActionGroup();
        flushToolGroup();
        if (pendingReasoning) {
          result.push({
            type: "toolGroup",
            entries: [],
            groupId: pendingReasoning.id,
            reasoning: pendingReasoning,
          });
          pendingReasoning = undefined;
        }
        result.push({ type: "single", entry });
      } else if (entry.entryType === "AssistantToolUse" && entry.toolCall) {
        flushUserActionGroup();
        currentToolGroup.push(entry);
      } else if (entry.entryType === "AssistantReasoning") {
        // Flush any accumulated groups before storing new reasoning
        flushUserActionGroup();
        flushToolGroup();
        pendingReasoning = entry;
      } else {
        // Flush any accumulated groups
        flushUserActionGroup();
        flushToolGroup();
        // If we have pending reasoning without tools, render it in a tool group
        if (pendingReasoning) {
          result.push({
            type: "toolGroup",
            entries: [],
            groupId: pendingReasoning.id,
            reasoning: pendingReasoning,
          });
          pendingReasoning = undefined;
        }
        result.push({ type: "single", entry });
      }
    }

    // Handle any remaining user action group at the end
    flushUserActionGroup();

    // Handle any remaining tool group at the end
    const remainingFirst = currentToolGroup[0];
    if (remainingFirst) {
      // Always group tools, but mark as trailing if AI is still working
      // This allows the collapse animation to play when AI finishes
      result.push({
        type: "toolGroup",
        entries: currentToolGroup,
        groupId: remainingFirst.id,
        reasoning: pendingReasoning,
        isTrailing: statusState !== "resting",
      });
    } else if (pendingReasoning) {
      // No tools followed the reasoning, render it in a tool group
      result.push({
        type: "toolGroup",
        entries: [],
        groupId: pendingReasoning.id,
        reasoning: pendingReasoning,
      });
    }

    return result;
  }, [entries, statusState]);

  // Clear expanded state when AI finishes (so new groups appear collapsed)
  useEffect(() => {
    if (statusState === "resting") {
      setExpandedToolGroups(new Set());
    }
  }, [statusState]);

  const withSelectedChatModel = useCallback(
    (payload: ChatSendPayload): ChatSendPayload => ({
      ...payload,
      chatModel: payload.chatModel ?? effectiveChatModel ?? undefined,
    }),
    [effectiveChatModel]
  );

  const send = useCallback(
    (payload: ChatSendPayload, restoreOnError?: string) => {
      onSend(withSelectedChatModel(payload), () => {
        if (restoreOnError) setInput((prev) => prev || restoreOnError);
        requestAnimationFrame(scrollComposerToEnd);
      });
      resetScroll();
    },
    [onSend, resetScroll, scrollComposerToEnd, setInput, withSelectedChatModel]
  );

  const releaseQueued = useCallback(
    (card: QueuedMessage, markFailed: () => void) => {
      const { id: _id, hasLostAttachments: _h, state: _s, ...payload } = card;
      playClick();
      onSend(withSelectedChatModel(payload), markFailed);
      resetScroll();
    },
    [onSend, withSelectedChatModel, resetScroll]
  );
  const [queue, setQueue] = useChatQueue(
    writeChatId,
    isActiveStatus,
    editingId != null,
    releaseQueued
  );
  useEffect(() => {
    if (selectedQueueItem == null) return;
    if (input !== "") setSelectedQueueItem(null);
    else if (selectedQueueItem.index >= queue.length)
      setSelectedQueueItem(null);
  }, [input, queue.length, selectedQueueItem]);
  useEffect(() => {
    if (editingId != null && !queue.some((x) => x.id === editingId))
      setEditingId(null);
  }, [editingId, queue]);

  const enqueueOrSend = (payload: ChatSendPayload, restoreOnError?: string) => {
    if (isActiveStatus) {
      setQueue((q) => [
        ...q,
        { id: nanoid(), ...withSelectedChatModel(payload) },
      ]);
    } else {
      send(payload, restoreOnError);
    }
  };

  useImperativeHandle(forwardedRef, () => ({
    enqueueOrSend,
  }));

  const forceSend = (card: QueuedMessage) => {
    const lastEntry = entries[entries.length - 1];
    const canInterrupt =
      lastEntry &&
      lastEntry.entryType !== "UserInterrupt" &&
      lastEntry.entryType !== "SystemMessage";
    if (canInterrupt && writeChatId && isResponding)
      abortChat(writeChatId, externalApiUrl);
    setQueue((q) => q.filter((x) => x.id !== card.id));
    const { id: _id, hasLostAttachments: _h, state: _s, ...payload } = card;
    send(
      {
        ...payload,
        ...(canInterrupt && { interruptEntryId: nanoid(12) }),
      },
      payload.text
    );
  };

  const cancelQueued = (id: string) => {
    setQueue((q) => q.filter((x) => x.id !== id));
  };

  const editQueued = (id: string, text: string) => {
    if (!text.trim()) return;
    setQueue((q) =>
      q.map((x) => (x.id === id ? { ...x, text, state: undefined } : x))
    );
  };

  const retryFromError = (errorEntryId: string) => {
    const idx = entries.findIndex((e) => e.id === errorEntryId);
    const preceding = idx === -1 ? entries : entries.slice(0, idx);
    const lastUser = preceding
      .filter((e) => e.entryType === "UserMessage" && e.userMessage?.text)
      .at(-1);
    const text = lastUser?.userMessage?.text;
    if (!text) return;
    enqueueOrSend(
      {
        text,
        isDictated: !!lastUser?.userMessage?.isDictated,
        planMode,
        effort: effort ?? undefined,
      },
      text
    );
  };

  const doSend = (force = false) => {
    const hasText = input.trim().length > 0;
    const readyAttachments = pendingAttachments.filter(
      (a) => a.data != null
    ) as ReadyAttachment[];
    const hasAttachments = readyAttachments.length > 0;
    const hasSnippets = (codeSnippets ?? []).length > 0;
    const hasElementSnippets = (elementSnippets ?? []).length > 0;
    const hasSkills = selectedSkills.length > 0;
    if (
      !hasText &&
      !hasAttachments &&
      !hasSnippets &&
      !hasElementSnippets &&
      !hasSkills
    )
      return;

    const attachments = toAttachmentPayload(readyAttachments);

    let text = input.trim() || (hasAttachments ? "See attached file" : "");

    if (hasSnippets) {
      const blocks = codeSnippets!.map((s) => {
        const lines =
          s.fromLine === s.toLine
            ? `${s.fromLine}`
            : `${s.fromLine}-${s.toLine}`;
        const displayPath = s.filePath.split("/").slice(1).join("/");
        const revisionAttr =
          s.revision === "previous" ? ` revision="previous"` : "";
        return `<code-context file="${displayPath}" lines="${lines}"${revisionAttr} color="${s.color}">\n${s.text}\n</code-context>`;
      });
      text = blocks.join("\n\n") + (text ? `\n\n${text}` : "");
    }

    if (hasElementSnippets) {
      const blocks = elementSnippets!.map(
        (s) =>
          `<element-context step="${s.stepName}" element="${s.descriptor}" color="${s.color}" index="${s.index}">\n<outer-html>\n${s.outerHtml}\n</outer-html>\n<text-content>\n${s.textContent}\n</text-content>\n</element-context>`
      );
      text = blocks.join("\n\n") + (text ? `\n\n${text}` : "");
    }

    text = prependAttachedSkills(text, selectedSkills);

    const payload = {
      text: text || "See selected element",
      selectedStep: selectedStepName,
      planMode,
      effort: effort ?? undefined,
      attachments,
    };
    const draftBeforeClear = input;
    if (force && isActiveStatus) {
      const lastEntry = entries[entries.length - 1];
      const canInterrupt =
        lastEntry &&
        lastEntry.entryType !== "UserInterrupt" &&
        lastEntry.entryType !== "SystemMessage";
      if (canInterrupt && writeChatId && isResponding)
        abortChat(writeChatId, externalApiUrl);
      send(
        {
          ...payload,
          ...(canInterrupt && { interruptEntryId: nanoid(12) }),
        },
        draftBeforeClear
      );
    } else {
      enqueueOrSend(payload, draftBeforeClear);
    }
    setInput("");
    requestAnimationFrame(updateComposerFade);
    clearAttachments();
    clearCodeSnippets?.();
    clearElementSnippets?.();
    clearSkills();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSend(resolveForce(false));
  };

  const pendingPromptTool = useMemo(() => {
    if (statusState !== "waiting") return null;
    const lastTool = (entries as ChatEntryWithSubTables[]).findLast((entry) => {
      if (entry.entryType !== "AssistantToolUse") return false;
      const call = entry.toolCall;
      if (!call || call.completedAt != null) return false;
      return !!toolsByName.get(call.toolName)?.renderPendingPrompt;
    });
    if (!lastTool?.toolCall) return null;
    const tool = toolsByName.get(lastTool.toolCall.toolName);
    if (!tool?.renderPendingPrompt) return null;
    return {
      toolCallId: lastTool.id,
      toolName: lastTool.toolCall.toolName,
      input: lastTool.toolCall.input,
      renderPendingPrompt: tool.renderPendingPrompt,
    };
  }, [entries, statusState, toolsByName]);

  const autoResolvedTool = useMemo(() => {
    if (isResponding) return null;
    const lastTool = (entries as ChatEntryWithSubTables[]).findLast((entry) => {
      if (entry.entryType !== "AssistantToolUse") return false;
      const call = entry.toolCall;
      if (!call || call.completedAt != null) return false;
      return (
        CLIENT_AUTO_RESOLVED_TOOLS.has(call.toolName) &&
        !!toolsByName.get(call.toolName)?.renderPendingPrompt
      );
    });
    if (!lastTool?.toolCall) return null;
    const tool = toolsByName.get(lastTool.toolCall.toolName);
    if (!tool?.renderPendingPrompt) return null;
    return {
      toolCallId: lastTool.id,
      input: lastTool.toolCall.input,
      renderPendingPrompt: tool.renderPendingPrompt,
    };
  }, [entries, isResponding, toolsByName]);

  const handleProvideToolResult = (
    entryId: string,
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean }
  ) => {
    if (!writeChatId) return;
    cacheToolOutput(entryId, output);
    onProvideToolResult(entryId, output, isError, {
      ...options,
      chatModel: effectiveChatModel ?? undefined,
    });
    resetScroll();
  };

  const pendingPromptNode =
    pendingPromptTool?.renderPendingPrompt({
      toolCallId: pendingPromptTool.toolCallId,
      input: pendingPromptTool.input,
      onResult: (output, isError, options) => {
        if (
          pendingPromptTool.toolName === "approvePlan" &&
          !isError &&
          output &&
          typeof output === "object" &&
          !Array.isArray(output) &&
          (output as { approved?: unknown }).approved === true
        ) {
          planModeTouchedRef.current = false;
          savePlanMode(writeChatId, false);
          setPlanMode(false);
        }
        handleProvideToolResult(
          pendingPromptTool.toolCallId,
          output,
          isError,
          options
        );
      },
    }) ?? null;

  const autoResolvedToolNode =
    autoResolvedTool?.renderPendingPrompt({
      toolCallId: autoResolvedTool.toolCallId,
      input: autoResolvedTool.input,
      onResult: (output, isError, options) =>
        handleProvideToolResult(
          autoResolvedTool.toolCallId,
          output,
          isError,
          options
        ),
    }) ?? null;

  const handleSelectSuggestion = (prompt: string) => {
    send({ text: prompt, planMode, effort: effort ?? undefined }, prompt);
  };

  return (
    <Container data-outer-scroll={outerScroll || undefined}>
      <div
        ref={messagesWrapperRef}
        style={{ flex: 1, minHeight: 0, position: "relative" }}
      >
        <Messages
          ref={outerScroll ? undefined : scrollContainer}
          data-outer-scroll={outerScroll || undefined}
          onMouseDownCapture={(e) => {
            if (entries.length === 0) {
              e.preventDefault();
              textareaRef.current?.focus();
            }
          }}
          style={
            !isCurrentUserChat
              ? { paddingBottom: 16 }
              : queueOverlayHeight > 0
                ? { paddingBottom: 56 + queueOverlayHeight + 12 }
                : undefined
          }
        >
          <div
            ref={topSentinelRef}
            style={{ height: 0, overflow: "hidden", flexShrink: 0 }}
          />
          {prevCheckpointLink && prevCheckpointLabel && (
            <PrevCheckpointLink
              to={prevCheckpointLink}
              data-visible={topVisible}
            >
              <Icon name="rotate-ccw" size={10} color="var(--text-secondary)" />
              <Text variant="text-small" color="var(--text-secondary)">
                {prevCheckpointLabel}
              </Text>
            </PrevCheckpointLink>
          )}
          {groupedEntries.map((grouped, index) => {
            const nextGrouped = groupedEntries[index + 1];
            const prevGrouped = groupedEntries[index - 1];

            const currentTimestamp = getGroupTimestamp(grouped);
            const prevTimestamp = prevGrouped
              ? getGroupTimestamp(prevGrouped)
              : null;
            const showDateMarker =
              currentTimestamp > 0 &&
              (prevTimestamp === null ||
                getDateKey(currentTimestamp) !== getDateKey(prevTimestamp));
            const dateMarker = showDateMarker ? (
              <DateMarker key={`date-${getDateKey(currentTimestamp)}`}>
                <DateBadge>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {formatDateLabel(currentTimestamp)}
                  </Text>
                </DateBadge>
              </DateMarker>
            ) : null;

            if (grouped.type === "userActionGroup") {
              return (
                <Fragment key={grouped.groupId}>
                  {dateMarker}
                  <Message
                    style={{
                      marginTop: prevGrouped ? 16 : 0,
                      marginBottom: nextGrouped ? 32 : 0,
                    }}
                  >
                    <UserActionGroup
                      entries={grouped.entries}
                      collapsed={!expandedUserActionGroups.has(grouped.groupId)}
                      toggleCollapsed={() =>
                        setExpandedUserActionGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(grouped.groupId)) {
                            next.delete(grouped.groupId);
                          } else {
                            next.add(grouped.groupId);
                          }
                          return next;
                        })
                      }
                    />
                  </Message>
                </Fragment>
              );
            }

            if (grouped.type === "toolGroup") {
              const isReasoningOnly =
                grouped.entries.length === 0 &&
                !!grouped.reasoning?.assistantReasoning;
              const nextEntry =
                nextGrouped?.type === "single" ? nextGrouped.entry : undefined;
              const nextIsAssistantMessage =
                nextEntry?.entryType === "AssistantMessage";
              const prevIsUserMessage =
                prevGrouped?.type === "single" &&
                prevGrouped.entry.entryType === "UserMessage";
              const toolGroupMarginBottom =
                nextGrouped && isReasoningOnly && nextIsAssistantMessage
                  ? 12
                  : nextGrouped
                    ? 32
                    : 0;
              const toolGroupMarginTop =
                isReasoningOnly && prevIsUserMessage ? 12 : 0;

              return (
                <Fragment key={grouped.groupId}>
                  {dateMarker}
                  <Message
                    style={{
                      marginBottom: toolGroupMarginBottom,
                      ...(toolGroupMarginTop > 0 && {
                        marginTop: toolGroupMarginTop,
                      }),
                    }}
                  >
                    <ToolUseGroup
                      entries={grouped.entries}
                      reasoning={grouped.reasoning}
                      collapsed={
                        grouped.isTrailing
                          ? false
                          : !expandedToolGroups.has(grouped.groupId)
                      }
                      toggleCollapsed={() =>
                        setExpandedToolGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(grouped.groupId)) {
                            next.delete(grouped.groupId);
                          } else {
                            next.add(grouped.groupId);
                          }
                          return next;
                        })
                      }
                      expandedToolUse={expandedToolUse}
                      setExpandedToolUse={setExpandedToolUse}
                      tools={toolsByName}
                      isTrailing={grouped.isTrailing}
                      onProvideResult={handleProvideToolResult}
                      streamingEntries={streamingEntries}
                    />
                  </Message>
                </Fragment>
              );
            }

            const entry = grouped.entry;

            const kickoffKind = entry.userMessage?.actionKind;
            if (
              entry.entryType === "UserMessage" &&
              entry.userMessage &&
              kickoffKind &&
              kickoffKind in kickoffDisplay
            ) {
              const kickoff =
                kickoffDisplay[kickoffKind as keyof typeof kickoffDisplay];
              return (
                <Fragment key={entry.id}>
                  {dateMarker}
                  <Message
                    style={{
                      marginTop: prevGrouped ? 16 : 0,
                      marginBottom: nextGrouped ? 16 : 0,
                    }}
                  >
                    <KickoffMessage
                      text={entry.userMessage.text}
                      icon={kickoff.icon}
                      label={kickoff.label}
                    />
                  </Message>
                </Fragment>
              );
            }

            const actionLine = ((): {
              icon: IconName;
              iconColor?: string;
              content: React.ReactNode;
            } | null => {
              if (entry.entryType === "UserAction" && entry.userAction) {
                const action = entry.userAction;
                if (action.actionType === "pushDraftToLive") {
                  const user = action.user;
                  return {
                    icon: "rocket",
                    content: (
                      <>
                        <Text
                          variant="text-small-md"
                          color="var(--text-primary)"
                        >
                          {user ? userDisplayName(user) : "Someone"}
                        </Text>{" "}
                        pushed{" "}
                        <Text
                          variant="text-small-md"
                          color="var(--text-primary)"
                        >
                          {action.metadata.draftName}
                        </Text>{" "}
                        live
                      </>
                    ),
                  };
                }
                const { icon, iconColor, text } = getActionDisplay(action);
                return { icon, iconColor, content: text };
              }
              const actionKind = entry.userMessage?.actionKind;
              if (
                entry.entryType === "UserMessage" &&
                actionKind &&
                actionKind in userMessageActionDisplay
              ) {
                const user = entry.userMessage?.user;
                const display =
                  userMessageActionDisplay[
                    actionKind as keyof typeof userMessageActionDisplay
                  ];
                return {
                  icon: display.icon,
                  content: (
                    <>
                      <Text variant="text-small-md" color="var(--text-primary)">
                        {user ? userDisplayName(user) : "Someone"}
                      </Text>{" "}
                      {display.message}
                    </>
                  ),
                };
              }
              return null;
            })();

            if (actionLine) {
              return (
                <Fragment key={entry.id}>
                  {dateMarker}
                  <Message
                    style={{
                      marginTop: prevGrouped ? 16 : 0,
                      marginBottom: nextGrouped ? 16 : 0,
                    }}
                  >
                    <ActivityLine
                      icon={actionLine.icon}
                      iconColor={actionLine.iconColor}
                    >
                      {actionLine.content}
                    </ActivityLine>
                  </Message>
                </Fragment>
              );
            }

            const nextEntry =
              nextGrouped?.type === "single" ? nextGrouped.entry : undefined;
            const isUser = entry.entryType === "UserMessage";
            const roleChange =
              nextEntry &&
              (entry.entryType === "UserMessage") !==
                (nextEntry.entryType === "UserMessage");
            const typeChange =
              nextEntry && entry.entryType !== nextEntry.entryType;
            const nextIsToolGroup = nextGrouped?.type === "toolGroup";

            return (
              <Fragment key={entry.id}>
                {dateMarker}
                <Message
                  style={{
                    ...(isUser && {
                      maxWidth: "85%",
                      alignSelf: "flex-end",
                      alignItems: "flex-end",
                      marginBottom: 32,
                    }),
                    ...(roleChange || (isUser && nextIsToolGroup)
                      ? { marginBottom: 32 }
                      : nextIsToolGroup && entry.entryType === "SystemMessage"
                        ? { marginBottom: 2 }
                        : (typeChange || nextIsToolGroup) && {
                            marginBottom: 12,
                          }),
                  }}
                >
                  {entry.entryType === "UserMessage" &&
                    entry.userMessage &&
                    (() => {
                      const messageUser = entry.userMessage.user;
                      const messageServiceAccount =
                        entry.userMessage.serviceAccount;
                      const author = messageUser ?? messageServiceAccount;
                      const userColor =
                        isGroupChat && author ? getUserColor(author.id) : null;

                      return (
                        <UserMessageWrapper data-user-message-id={entry.id}>
                          <UserMessageBubble
                            text={entry.userMessage.text ?? ""}
                            isDictated={!!entry.userMessage.isDictated}
                            attachments={entry.attachments}
                            userColor={userColor}
                          />
                          {isGroupChat && author && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: -6,
                                right: -6,
                              }}
                            >
                              <Tooltip
                                content={
                                  messageUser
                                    ? messageUser.id === z.userID
                                      ? "You"
                                      : userDisplayName(messageUser)
                                    : messageServiceAccount!.name
                                }
                              >
                                {messageUser ? (
                                  <Avatar
                                    user={messageUser}
                                    size={12}
                                    tooltip={false}
                                    bare={false}
                                  />
                                ) : (
                                  <ServiceAccountAvatar
                                    seed={messageServiceAccount!.id}
                                    size={12}
                                  />
                                )}
                              </Tooltip>
                            </div>
                          )}
                          <UserMessageTimestamp>
                            {formatHoverTime(entry.createdAt)}
                          </UserMessageTimestamp>
                        </UserMessageWrapper>
                      );
                    })()}

                  {entry.entryType === "AssistantMessage" &&
                    entry.assistantMessage &&
                    (() => {
                      const text =
                        streamingEntries.get(entry.id)?.text ??
                        entry.assistantMessage.text;
                      const isStreaming =
                        streamingEntries.get(entry.id)?.status === "streaming";
                      const errorDetail = isStreaming
                        ? null
                        : parseChatError(text);
                      if (errorDetail != null) {
                        return (
                          <ErrorMessage
                            message={errorDetail}
                            createdAt={entry.createdAt}
                            canRetry={isCurrentUserChat && canWrite}
                            onRetry={() => retryFromError(entry.id)}
                          />
                        );
                      }
                      return (
                        <TypewriterText
                          text={text}
                          streaming={isStreaming}
                          onAnimatingChange={handleTypewriterAnimatingChange}
                          resolveFileHref={resolveFileHref}
                        />
                      );
                    })()}

                  {entry.entryType === "Compaction" && entry.compaction && (
                    <CompactionEntry />
                  )}

                  {entry.entryType === "AssistantToolUse" &&
                    entry.toolCall?.toolName === "approvePlan" &&
                    (() => {
                      const plan = parseApprovePlanInput(entry.toolCall.input);
                      if (!plan) return null;
                      return (
                        <TypewriterText
                          text={`**${plan.title}**\n\n${plan.plan}`}
                          streaming={false}
                        />
                      );
                    })()}

                  {entry.entryType === "SystemMessage" &&
                    entry.systemMessage &&
                    (entry.systemMessage.text.startsWith("Merged") ? (
                      <MergeMessage text={entry.systemMessage.text} />
                    ) : entry.systemMessage.text.startsWith(
                        "Synced from git"
                      ) ? (
                      <GitSyncMessage text={entry.systemMessage.text} />
                    ) : isPushMessage(entry.systemMessage.text) ? (
                      <PushMessage text={entry.systemMessage.text} />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: 28,
                          gap: 4,
                          padding: "6px 10px 6px 8px",
                          borderRadius: 8,
                          width: "fit-content",
                          ...(entry.systemMessage.text.startsWith("Saved:") && {
                            marginTop: 16,
                          }),
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Icon
                            name={systemMessageIcon(entry.systemMessage.text)}
                            size={12}
                            color="var(--text-secondary)"
                          />
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {entry.systemMessage.text === CONTEXT_LIMIT_MESSAGE
                              ? "Reached the context limit"
                              : entry.systemMessage.text}
                          </Text>
                        </div>
                      </div>
                    ))}

                  {entry.entryType === "AssistantToolUse" &&
                    entry.toolCall &&
                    entry.toolCall.toolName !== "approvePlan" && (
                      <ToolUse
                        toolUse={{
                          id: entry.id,
                          toolName: entry.toolCall.toolName,
                          input: entry.toolCall.input,
                          output: entry.toolCall.output,
                          isError: entry.toolCall.isError,
                        }}
                        result={
                          entry.toolCall.completedAt != null
                            ? {
                                id: entry.id,
                                output: entry.toolCall.output,
                                isError: entry.toolCall.isError ?? false,
                              }
                            : null
                        }
                        expanded={expandedToolUse === entry.id}
                        toggleExpanded={() =>
                          setExpandedToolUse((was) =>
                            was === entry.id ? null : entry.id
                          )
                        }
                        toolAfter={nextEntry?.entryType === "AssistantToolUse"}
                        tool={toolsByName.get(entry.toolCall.toolName)}
                        createdAt={entry.createdAt}
                        completedAt={entry.toolCall.completedAt}
                        onProvideResult={handleProvideToolResult}
                      />
                    )}

                  {/* UserAction entries are now grouped via userActionGroup */}
                </Message>
              </Fragment>
            );
          })}
          {modelFeedbackEligible &&
            statusState === "resting" &&
            entries.some((e) => e.entryType === "UserMessage") && (
              <ModelFeedbackRow
                submitted={experimentState.feedback != null}
                onFeedback={submitModelFeedback}
              />
            )}
          {nextCheckpointLink && nextCheckpointLabel && (
            <>
              <div ref={bottomSentinelRef} />
              <NextCheckpointLink
                to={nextCheckpointLink}
                data-visible={bottomVisible}
              >
                <Icon
                  name="rotate-cw"
                  size={10}
                  color="var(--text-secondary)"
                />
                <Text variant="text-small" color="var(--text-secondary)">
                  {nextCheckpointLabel}
                </Text>
              </NextCheckpointLink>
            </>
          )}
        </Messages>
        {isCurrentUserChat &&
          entries.length === 0 &&
          emptyState?.({ fillInput, visible: input === "" })}
        {!outerScroll && (
          <ChatMessageNav
            entries={entries as ChatEntryWithSubTables[]}
            scrollContainer={scrollContainer}
            wrapperRef={messagesWrapperRef}
          />
        )}
        {isCurrentUserChat && canWrite && (
          <QueueStack
            queue={queue}
            overlayRef={setQueueOverlayRef}
            onForceSend={forceSend}
            onCancel={cancelQueued}
            scrollContainer={scrollContainer}
            selectedIndex={selectedQueueItem?.index ?? null}
            selectedButton={selectedQueueItem?.button ?? null}
            editingId={editingId}
            onEditStart={(id) => setEditingId(id)}
            onEditCommit={(id, text) => {
              editQueued(id, text);
              setEditingId(null);
              textareaRef.current?.focus();
            }}
            onEditCancel={() => {
              setEditingId(null);
              textareaRef.current?.focus();
            }}
          />
        )}
      </div>

      {isCurrentUserChat && (
        <ChatForm
          onSubmit={handleSubmit}
          data-outer-scroll={outerScroll || undefined}
        >
          <TextareaContainer
            style={{
              width: "100%",
              ...{
                padding: 4,
                margin: -4.5,
                background:
                  "linear-gradient(var(--alpha2), var(--alpha2)) padding-box, light-dark(var(--surface-secondary), var(--surface-primary))",
                border: "0.5px solid var(--border-alpha)",
              },
            }}
          >
            {isScrolledUp && (
              <ScrollToBottomButton
                onPress={scrollToBottom}
                style={
                  queueOverlayHeight > 0
                    ? {
                        transform: `translate(-50%, calc(-100% - 12px - ${queueOverlayHeight + 12}px))`,
                      }
                    : undefined
                }
              >
                <Icon name="arrow-down" size={12} />
              </ScrollToBottomButton>
            )}
            <StatusContainer>
              {onStatusClick ? (
                <FollowNudge
                  show={showFollowNudge}
                  trigger={({ style }) => (
                    <span style={{ ...style, display: "inline-flex" }}>
                      <Tooltip
                        content={
                          followActive ? "Stop following 3B" : "Follow 3B"
                        }
                        hidden={showFollowNudge}
                      >
                        <FollowAvatarButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDismissFollowNudge?.();
                            onStatusClick();
                          }}
                          aria-label={
                            followActive ? "Stop following 3B" : "Follow 3B"
                          }
                        >
                          <StatusIndicator
                            state={isDragging ? "dragging" : statusState}
                            focused={
                              isDragging || isTextareaFocused || isRecording
                            }
                            planMode={planMode}
                            celebrateTrigger={celebrateTrigger}
                          />
                        </FollowAvatarButton>
                      </Tooltip>
                    </span>
                  )}
                />
              ) : (
                <StatusIndicator
                  state={isDragging ? "dragging" : statusState}
                  focused={isDragging || isTextareaFocused || isRecording}
                  planMode={planMode}
                  celebrateTrigger={celebrateTrigger}
                />
              )}
              <Text
                variant="text-small-md"
                ellipsis
                color="var(--text-primary)"
                style={{
                  opacity:
                    isDragging ||
                    isRecording ||
                    isActiveStatus ||
                    isTextareaFocused ||
                    planMode
                      ? 1
                      : 0.2,
                }}
              >
                {isDragging ? (
                  statusText.dragging
                ) : isRecording ? (
                  "Listening"
                ) : (
                  <StatusLabel
                    statusState={statusState}
                    respondingAt={respondingAt}
                    lastLoopHeartbeatAt={lastLoopHeartbeatAt}
                    planMode={planMode}
                  />
                )}
              </Text>
              {isActiveStatus && !isRecording && respondingAt && (
                <StatusTimer respondingAt={respondingAt} />
              )}
              {isRecording && (
                <RecordingIndicator>
                  <RecordingDot />
                  <Text variant="text-small-md" color="var(--text-secondary)">
                    {formatElapsedTime(recordingElapsedTime)}
                  </Text>
                </RecordingIndicator>
              )}
            </StatusContainer>

            <AttachmentRowAnimator
              visible={
                pendingAttachments.length > 0 ||
                (codeSnippets ?? []).length > 0 ||
                (elementSnippets ?? []).length > 0 ||
                selectedSkills.length > 0
              }
            >
              <AttachmentPreviewRow>
                {selectedSkills.map((skill) => (
                  <AttachmentPreview
                    key={skill.id}
                    onPress={() => removeSkill(skill.id)}
                  >
                    <span className="file-icon">
                      <Icon name="graduation-cap" size={10} />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {skill.name}
                    </Text>
                  </AttachmentPreview>
                ))}
                {pendingAttachments.map((att) => (
                  <AttachmentHoverPreview
                    key={att.id}
                    fileName={att.fileName}
                    mediaType={att.mediaType}
                    sizeBytes={att.sizeBytes}
                    url={att.previewUrl}
                    inlineData={att.data}
                    onRemove={() => removeAttachment(att.id)}
                  >
                    <AttachmentPreview onPress={() => removeAttachment(att.id)}>
                      <span className="file-icon">
                        <Icon
                          name={attachmentFileIcon(att.mediaType)}
                          size={10}
                        />
                      </span>
                      <span className="remove-icon">
                        <Icon name="x" size={10} />
                      </span>
                      <Text variant="text-small-md" ellipsis>
                        {att.fileName}
                      </Text>
                    </AttachmentPreview>
                  </AttachmentHoverPreview>
                ))}
                {(codeSnippets ?? []).map((snippet) => (
                  <AttachmentPreview
                    key={snippet.id}
                    onPress={() => removeCodeSnippet?.(snippet.id)}
                  >
                    <span className="file-icon">
                      <AccentBar
                        style={{
                          color: `var(--${snippet.color}-600)`,
                        }}
                      />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {snippet.filePath.split("/").slice(1).join("/")}:{" "}
                      {snippet.fromLine === snippet.toLine
                        ? snippet.fromLine
                        : `${snippet.fromLine}–${snippet.toLine}`}
                    </Text>
                  </AttachmentPreview>
                ))}
                {(elementSnippets ?? []).map((snippet) => (
                  <AttachmentPreview
                    key={snippet.id}
                    onPress={() => removeElementSnippet?.(snippet.id)}
                    onMouseEnter={() =>
                      window.dispatchEvent(
                        new CustomEvent("3b-highlight-element", {
                          detail: { index: snippet.index },
                        })
                      )
                    }
                    onMouseLeave={() =>
                      window.dispatchEvent(
                        new CustomEvent("3b-highlight-element", {
                          detail: { index: null },
                        })
                      )
                    }
                  >
                    <span
                      className="file-icon"
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: `var(--${snippet.color}-600)`,
                        color: "white",
                        fontSize: 9,
                        fontWeight: 500,
                        flexShrink: 0,
                        marginLeft: -3,
                      }}
                    >
                      {snippet.index}
                    </span>
                    <span
                      className="remove-icon"
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: `var(--${snippet.color}-600)`,
                        flexShrink: 0,
                        marginLeft: -3,
                      }}
                    >
                      <Icon name="x" size={10} color="white" />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {snippet.label || snippet.descriptor}
                    </Text>
                  </AttachmentPreview>
                ))}
              </AttachmentPreviewRow>
            </AttachmentRowAnimator>

            {autoResolvedToolNode}
            {pendingPromptOverride ? (
              pendingPromptOverride
            ) : pendingPromptNode ? (
              pendingPromptNode
            ) : showSuggestions ? (
              <SlashMenu
                onSelect={handleSelectSuggestion}
                onSelectSkill={(skill) => {
                  addSkill(skill);
                  requestAnimationFrame(() => textareaRef.current?.focus());
                }}
                onDismiss={() => setShowSuggestions(false)}
                suggestions={suggestions}
                skills={availableSkills}
                canCreateSkill={canCreateSkill}
                onCreateSkill={() => {
                  const id = createSkill();
                  if (id) navigate(routes.modal.skill, { id: id });
                }}
              />
            ) : isRecording ? (
              <VoiceRecorderContainer>
                <VoiceRecorder
                  leftControls={
                    <PlanModeToggle
                      planMode={planMode}
                      onToggle={togglePlanMode}
                    />
                  }
                  onTranscript={(text) => {
                    const trimmed = text.trim();
                    setInput(trimmed);
                    requestAnimationFrame(() => {
                      const el = textareaRef.current;
                      if (el) {
                        el.focus();
                        el.setSelectionRange(trimmed.length, trimmed.length);
                      }
                      scrollComposerToEnd();
                    });
                  }}
                  onCancel={() => {
                    setIsRecording(false);
                    setRecordingStartTime(null);
                  }}
                  onSend={(text) => {
                    setIsRecording(false);
                    setRecordingStartTime(null);
                    const readyAttachments = pendingAttachments.filter(
                      (a) => a.data != null
                    ) as ReadyAttachment[];
                    if (!text && readyAttachments.length === 0) return;
                    const attachments = toAttachmentPayload(readyAttachments);
                    enqueueOrSend(
                      {
                        text: text || "See attached file",
                        isDictated: true,
                        planMode,
                        effort: effort ?? undefined,
                        attachments,
                      },
                      text
                    );
                    setInput("");
                    requestAnimationFrame(updateComposerFade);
                    clearAttachments();
                  }}
                />
              </VoiceRecorderContainer>
            ) : (
              <ComposerInputWrap data-show-fade={showComposerFade || undefined}>
                <Textarea
                  autoFocus={!isMobile}
                  disabled={!canWrite}
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    const shouldFollowCaret =
                      e.target.selectionStart === e.target.value.length &&
                      e.target.selectionEnd === e.target.value.length;
                    setInput(e.target.value);
                    syncSlashToken(e.target);
                    requestAnimationFrame(
                      shouldFollowCaret
                        ? scrollComposerToEnd
                        : updateComposerFade
                    );
                  }}
                  onKeyUp={(e) => syncSlashToken(e.currentTarget)}
                  onClick={(e) => syncSlashToken(e.currentTarget)}
                  onSelect={(e) => syncSlashToken(e.currentTarget)}
                  onScroll={updateComposerFade}
                  placeholder={
                    isDragging
                      ? ""
                      : suggestions.items.length > 0 ||
                          availableSkills.length > 0
                        ? `${placeholder}, or type / for skills and prompts…`
                        : `${placeholder}…`
                  }
                  onFocus={() => setIsTextareaFocused(true)}
                  onBlur={() => setIsTextareaFocused(false)}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    if (handleSlashKeyDown(e)) return;
                    if (
                      e.key === "/" &&
                      !e.metaKey &&
                      !e.ctrlKey &&
                      input === "" &&
                      (suggestions.items.length > 0 ||
                        availableSkills.length > 0)
                    ) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      return;
                    }
                    if (input === "" && queue.length > 0) {
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setSelectedQueueItem((prev) => ({
                          index:
                            prev == null
                              ? queue.length - 1
                              : Math.max(0, prev.index - 1),
                          button: prev?.button ?? "send",
                        }));
                        return;
                      }
                      if (e.key === "ArrowDown" && selectedQueueItem != null) {
                        e.preventDefault();
                        if (selectedQueueItem.index >= queue.length - 1) {
                          setSelectedQueueItem(null);
                        } else {
                          setSelectedQueueItem({
                            ...selectedQueueItem,
                            index: selectedQueueItem.index + 1,
                          });
                        }
                        return;
                      }
                      if (selectedQueueItem != null) {
                        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                          e.preventDefault();
                          const order = ["cancel", "edit", "send"] as const;
                          const delta = e.key === "ArrowLeft" ? -1 : 1;
                          const idx = Math.min(
                            order.length - 1,
                            Math.max(
                              0,
                              order.indexOf(selectedQueueItem.button) + delta
                            )
                          );
                          setSelectedQueueItem({
                            ...selectedQueueItem,
                            button: order[idx]!,
                          });
                          return;
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const card = queue[selectedQueueItem.index];
                          if (card) {
                            playClick();
                            if (selectedQueueItem.button === "send")
                              forceSend(card);
                            else if (selectedQueueItem.button === "edit")
                              setEditingId(card.id);
                            else cancelQueued(card.id);
                          }
                          setSelectedQueueItem(null);
                          return;
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedQueueItem(null);
                          return;
                        }
                      }
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      doSend(resolveForce(e.metaKey || e.ctrlKey));
                    }
                    if (e.key === "Escape") {
                      e.currentTarget.blur();
                      e.stopPropagation();
                    }
                  }}
                  minRows={4}
                  maxRows={20}
                  style={{
                    minHeight: 96,
                    paddingRight: 12,
                    paddingBottom: 44,
                    ...(isDragging && { color: "transparent" }),
                    // While a completion shows, the overlay renders the text so the ghost can push what follows the caret; the textarea keeps only the caret.
                    ...(slashCompletion.suffix !== "" && {
                      color: "transparent",
                      caretColor: "var(--text-primary)",
                    }),
                  }}
                />
                <SlashSkillCompletion
                  textareaRef={textareaRef}
                  value={input}
                  isTextareaFocused={isTextareaFocused}
                  completion={slashCompletion}
                />
                {!isDragging && (
                  <ComposerControls>
                    <ComposerControlGroup>
                      <PlanModeToggle
                        planMode={planMode}
                        onToggle={togglePlanMode}
                      />
                    </ComposerControlGroup>
                    <ComposerStripToggle />
                    <ComposerControlGroup>
                      <Tooltip content="Voice mode">
                        <Button
                          type="button"
                          icon="mic"
                          variant="secondary"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setIsRecording(true);
                            setRecordingStartTime(Date.now());
                            setRecordingElapsedTime(0);
                          }}
                          disabled={!isCurrentUserChat}
                        />
                      </Tooltip>
                      {isActiveStatus && writeChatId && !input.trim() ? (
                        <Tooltip content="Stop">
                          <StopButton
                            type="button"
                            icon="stop"
                            variant="secondary"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              const lastEntry = entries[entries.length - 1];
                              if (
                                lastEntry &&
                                lastEntry.entryType !== "UserInterrupt" &&
                                lastEntry.entryType !== "SystemMessage"
                              ) {
                                abortChat(writeChatId, externalApiUrl);
                                onInterrupt();
                              }
                            }}
                            disabled={!canWrite}
                          />
                        </Tooltip>
                      ) : (
                        <Button
                          icon="arrow-up"
                          type="button"
                          onClick={(e) =>
                            doSend(resolveForce(e.metaKey || e.ctrlKey))
                          }
                          disabled={
                            !input.trim() &&
                            pendingAttachments.length === 0 &&
                            (codeSnippets ?? []).length === 0 &&
                            (elementSnippets ?? []).length === 0
                          }
                        />
                      )}
                    </ComposerControlGroup>
                  </ComposerControls>
                )}
                {isDragging && (
                  <Dropzone
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                  >
                    <svg
                      width="100%"
                      height="100%"
                      style={{ position: "absolute", inset: 0 }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="calc(100% - 2px)"
                        height="calc(100% - 2px)"
                        rx="12"
                        ry="12"
                        fill="none"
                        stroke="var(--text-primary)"
                        strokeWidth="1"
                        strokeDasharray="6 4"
                        style={{
                          animation: `${marchingDashes} 0.5s linear infinite`,
                        }}
                      />
                    </svg>
                    <Icon
                      name="arrow-down"
                      size={10}
                      color="var(--text-primary)"
                    />
                    <Text variant="text-small" color="var(--text-primary)">
                      Drop files here
                    </Text>
                  </Dropzone>
                )}
              </ComposerInputWrap>
            )}
            <ComposerStrip
              onAddFiles={processFiles}
              modelScope={modelScope}
              effort={effort}
              onEffortChange={(next) => {
                saveEffort(writeChatId, next);
                setLocalEffort(next);
              }}
              chatModel={effectiveChatModel}
              onChatModelChange={handleChatModelChange}
            />
          </TextareaContainer>
        </ChatForm>
      )}
      {!isCurrentUserChat && historyBanner && (
        <div style={{ padding: "0 4px 4px", margin: "-0.5px" }}>
          {historyBanner}
        </div>
      )}
      {flyingFiles.map((f) => (
        <FlyingDot
          key={f.id}
          startX={f.x}
          startY={f.y}
          onDone={() => removeFlyingFile(f.id)}
        />
      ))}
    </Container>
  );
});
